import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Search,
  Refresh,
  CloudUpload,
  Download,
  SwapHoriz,
  Sync,
} from '@mui/icons-material';
import CustomerStatusSyncDialog from '../components/CustomerStatusSyncDialog';
import { useQuery } from '@tanstack/react-query';
import customerService from '../services/customerService';
import spopService from '../services/spopService';
import AddCustomerDialog from '../components/AddCustomerDialog';
import EditCustomerDialog from '../components/EditCustomerDialog';
import ViewCustomerDialog from '../components/ViewCustomerDialog';
import ExcelImportDialog from '../components/ExcelImportDialog';
import { format } from 'date-fns';
import { useAuth } from '../hooks/useAuth';

type PageLimit = 10 | 50 | 100 | 'all';

const Customers: React.FC = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState<PageLimit>(10);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedSpopFilter, setSelectedSpopFilter] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [excelImportDialogOpen, setExcelImportDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkCount, setBulkCount] = useState(0);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkMessage, setBulkMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  
  const { user } = useAuth();
  const canManage = user?.role === 'admin' || user?.role === 'operator';
  
  // Get SPOP filter for operators
  const operatorSpopFilter = user?.role === 'operator' && user?.spop_id ? user.spop_id : undefined;

  // Queries
  const { 
    data: customersData, 
    isLoading, 
    error, 
    refetch 
  } = useQuery({
    queryKey: ['customers', page, limit, search, statusFilter, selectedSpopFilter, operatorSpopFilter],
    queryFn: async () => {
      const filters: any = {};
      
      // Apply operator SPOP filter if user is operator
      if (operatorSpopFilter) {
        filters.spop_id = operatorSpopFilter;
      }
      // Apply selected SPOP filter if user is admin and has selected a SPOP
      else if (selectedSpopFilter) {
        filters.spop_id = selectedSpopFilter;
      }
      
      // Apply status filter
      if (statusFilter) {
        filters.status = statusFilter;
      }
      
      console.log('Filters being sent:', filters); // Debug log
      console.log('Query params:', { page, limit, search, filters }); // Debug log
      
      try {
        const result = await customerService.getCustomers(page, limit, search, Object.keys(filters).length > 0 ? filters : undefined);
        console.log('Query result:', result); // Debug log
        return result;
      } catch (error) {
        console.error('Query error:', error);
        throw error;
      }
    },
  });

  // Get SPOPs for filter (only for admin users)
  const { data: spopsData } = useQuery({
    queryKey: ['spops'],
    queryFn: async () => {
      const response = await spopService.getAllSPOPs();
      return response.data;
    },
    enabled: user?.role === 'admin', // Only fetch if user is admin
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'default';
      case 'suspended':
        return 'error';
      case 'free':
        return 'secondary';
      case 'NonInternet':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Aktif';
      case 'inactive':
        return 'Non-aktif';
      case 'suspended':
        return 'Ditangguhkan';
      case 'free':
        return 'Gratis';
      case 'NonInternet':
        return 'Non-Internet';
      default:
        return status;
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleClearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setSelectedSpopFilter('');
    setPage(1);
  };

  const customers = customersData?.data?.customers ?? [];

  // Reset page to 1 when filters or limit change
  React.useEffect(() => {
    setPage(1);
  }, [search, statusFilter, selectedSpopFilter, limit]);

  // Clear selection when page, filters, or limit change
  React.useEffect(() => {
    setSelectedIds([]);
  }, [page, search, statusFilter, selectedSpopFilter, limit]);

  // Debug effect to log status filter changes
  React.useEffect(() => {
    console.log('Status filter changed:', statusFilter);
  }, [statusFilter]);

  const handleAddCustomer = () => {
    setAddDialogOpen(true);
  };

  const handleExcelImport = () => {
    setExcelImportDialogOpen(true);
  };

  const handleExcelExport = async () => {
    setIsExporting(true);
    try {
      const filters: any = {};
      
      // Apply operator SPOP filter if user is operator
      if (operatorSpopFilter) {
        filters.spop_id = operatorSpopFilter;
      }
      // Apply selected SPOP filter if user is admin and has selected a SPOP
      else if (selectedSpopFilter) {
        filters.spop_id = selectedSpopFilter;
      }
      
      // Apply status filter
      if (statusFilter) {
        filters.status = statusFilter;
      }

      console.log('Exporting with filters:', filters); // Debug log
      
      await customerService.exportCustomers(search, Object.keys(filters).length > 0 ? filters : undefined);
    } catch (error) {
      console.error('Export error:', error);
      alert('Terjadi kesalahan saat mengekspor data: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleViewCustomer = (customer: any) => {
    setSelectedCustomer(customer);
    setViewDialogOpen(true);
  };

  const handleEditCustomer = (customer: any) => {
    setSelectedCustomer(customer);
    setEditDialogOpen(true);
  };

  const handleDeleteCustomer = (customer: any) => {
    // TODO: Implement delete customer functionality
    if (window.confirm(`Apakah Anda yakin ingin menghapus pelanggan ${customer.name}?`)) {
      alert(`Pelanggan ${customer.name} akan dihapus!`);
    }
  };

  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleToggleSelectAll = () => {
    const pageIds = customers.map((c: any) => c.id);
    const allSelected = pageIds.length > 0 && pageIds.every((id: string) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
    }
  };

  const isAllPageSelected =
    customers.length > 0 &&
    customers.every((c: any) => selectedIds.includes(c.id));

  const isSomePageSelected =
    customers.some((c: any) => selectedIds.includes(c.id)) && !isAllPageSelected;

  const handleOpenBulkDialog = () => {
    if (selectedIds.length === 0) return;
    setBulkCount(selectedIds.length);
    setBulkStatus('');
    setBulkMessage(null);
    setBulkDialogOpen(true);
  };

  const handleBulkStatusUpdate = async () => {
    if (!bulkStatus || selectedIds.length === 0) return;

    setBulkLoading(true);
    setBulkMessage(null);
    try {
      const result = await customerService.bulkUpdateStatus(
        selectedIds,
        bulkStatus as 'active' | 'inactive' | 'suspended' | 'free' | 'NonInternet'
      );
      setBulkMessage({
        type: result.failed > 0 ? 'error' : 'success',
        text: `${result.updated} pelanggan berhasil diubah ke ${getStatusLabel(bulkStatus)}${
          result.failed > 0 ? `, ${result.failed} gagal` : ''
        }`,
      });
      refetch();
      if (result.failed === 0) {
        setSelectedIds([]);
        setTimeout(() => setBulkDialogOpen(false), 1200);
      }
    } catch (error: any) {
      setBulkMessage({
        type: 'error',
        text: error.response?.data?.message || 'Gagal mengubah status pelanggan',
      });
    } finally {
      setBulkLoading(false);
    }
  };

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Error loading customers: {error instanceof Error ? error.message : 'Unknown error'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1">
            Manajemen Pelanggan
          </Typography>
          {user?.role === 'operator' && user?.spop && (
            <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 0.5 }}>
              SPOP: {user.spop.name} ({user.spop.areaCode})
            </Typography>
          )}
        </Box>
        <Box display="flex" gap={2} flexWrap="wrap">
          {canManage && (
            <Button
              variant="outlined"
              startIcon={<Sync />}
              onClick={() => setSyncDialogOpen(true)}
              disabled={customers.length === 0 && selectedIds.length === 0}
              color="warning"
            >
              Cek DB ↔ MikroTik
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExcelExport}
            color="success"
            disabled={isExporting}
          >
            {isExporting ? 'Mengekspor...' : 'Export Excel'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<CloudUpload />}
            onClick={handleExcelImport}
            color="info"
          >
            Import Excel
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddCustomer}
          >
            Tambah Pelanggan
          </Button>
        </Box>
      </Box>

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} alignItems="center">
            <Box flex={1} minWidth={200}>
              <TextField
                label="Cari pelanggan..."
                variant="outlined"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
                }}
                fullWidth
              />
            </Box>
            
            <Box minWidth={150}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => {
                    const newValue = e.target.value;
                    console.log('Status filter selected:', newValue); // Debug log
                    setStatusFilter(newValue);
                  }}
                >
                  <MenuItem value="">Semua Status</MenuItem>
                  <MenuItem value="active">Aktif</MenuItem>
                  <MenuItem value="inactive">Non-aktif</MenuItem>
                  <MenuItem value="suspended">Ditangguhkan</MenuItem>
                  <MenuItem value="free">Gratis</MenuItem>
                  <MenuItem value="NonInternet">Non-Internet</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {user?.role === 'admin' && (
              <Box minWidth={200}>
                <FormControl fullWidth>
                  <InputLabel>SPOP</InputLabel>
                  <Select
                    value={selectedSpopFilter}
                    label="SPOP"
                    onChange={(e) => setSelectedSpopFilter(e.target.value)}
                  >
                    <MenuItem value="">Semua SPOP</MenuItem>
                    {spopsData?.data?.spops && Array.isArray(spopsData.data.spops) && 
                      spopsData.data.spops.map((spop: any) => (
                        <MenuItem key={spop.id} value={spop.id}>
                          {spop.name} ({spop.areaCode})
                        </MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
              </Box>
            )}

            <Box display="flex" gap={1} flexWrap="wrap">
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                size="small"
                disabled={!search && !statusFilter && !selectedSpopFilter}
              >
                Reset Filter
              </Button>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={handleRefresh}
                size="small"
              >
                Refresh
              </Button>
            </Box>
          </Box>

          {/* Filter Summary */}
          {(search || statusFilter || selectedSpopFilter) && (
            <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1}>
              <Typography variant="body2" color="text.secondary">
                Filter aktif: {' '}
                {search && <Chip label={`Pencarian: "${search}"`} size="small" sx={{ mr: 1 }} />}
                {statusFilter && <Chip label={`Status: ${getStatusLabel(statusFilter)} (${statusFilter})`} size="small" sx={{ mr: 1 }} />}
                {selectedSpopFilter && spopsData?.data?.spops && (
                  <Chip 
                    label={`SPOP: ${spopsData.data.spops.find((s: any) => s.id == selectedSpopFilter)?.name || selectedSpopFilter}`} 
                    size="small" 
                    sx={{ mr: 1 }} 
                  />
                )}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {canManage && selectedIds.length > 0 && (
        <Card sx={{ mb: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.light' }}>
          <CardContent sx={{ py: '12px !important' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <Typography variant="body2" fontWeight={600}>
                {selectedIds.length} pelanggan dipilih
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<SwapHoriz />}
                onClick={handleOpenBulkDialog}
              >
                Ubah status massal
              </Button>
              <Button variant="text" size="small" onClick={() => setSelectedIds([])}>
                Batal pilih
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Customers Table */}
      <Card>
        <CardContent>
          {isLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    {canManage && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={isSomePageSelected}
                          checked={isAllPageSelected}
                          onChange={handleToggleSelectAll}
                        />
                      </TableCell>
                    )}
                    <TableCell>Kode Pelanggan</TableCell>
                    <TableCell>Username</TableCell>
                    <TableCell>Nama Lengkap</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Saldo</TableCell>
                    <TableCell>Router</TableCell>
                    <TableCell>SPOP</TableCell>
                    <TableCell>ODP</TableCell>
                    <TableCell>Tanggal Daftar</TableCell>
                    <TableCell>Aksi</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customers.length > 0 ?
                    customers.map((customer: any) => (
                    <TableRow
                      key={customer.id}
                      selected={selectedIds.includes(customer.id)}
                      hover
                    >
                      {canManage && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedIds.includes(customer.id)}
                            onChange={() => handleToggleSelect(customer.id)}
                          />
                        </TableCell>
                      )}
                      <TableCell>{customer.customer_code}</TableCell>
                      <TableCell>{customer.username}</TableCell>
                      <TableCell>{customer.name}</TableCell>
                      <TableCell>{customer.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(customer.status)}
                          color={getStatusColor(customer.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatCurrency(customer.balance)}</TableCell>
                      <TableCell>{customer.router?.name || '-'}</TableCell>
                      <TableCell>{customer.spop?.name || '-'}</TableCell>
                      <TableCell>{customer.odp?.fullCode || '-'}</TableCell>
                      <TableCell>
                        {format(new Date(customer.created_at), 'dd/MM/yyyy')}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Lihat Detail">
                          <IconButton 
                            size="small"
                            onClick={() => handleViewCustomer(customer)}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton 
                            size="small"
                            onClick={() => handleEditCustomer(customer)}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Hapus">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleDeleteCustomer(customer)}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={canManage ? 12 : 11} align="center">
                        Tidak ada data pelanggan
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {customersData?.data?.pagination && (
            <TablePagination
              component="div"
              count={customersData.data.pagination.total}
              page={limit === 'all' ? 0 : page - 1}
              onPageChange={(_, newPage) => setPage(newPage + 1)}
              rowsPerPage={limit === 'all' ? -1 : limit}
              onRowsPerPageChange={(e) => {
                const val = parseInt(e.target.value, 10);
                setLimit(val === -1 ? 'all' : (val as 10 | 50 | 100));
              }}
              rowsPerPageOptions={[
                10,
                50,
                100,
                { label: 'Semua', value: -1 },
              ]}
              labelRowsPerPage="Tampilkan:"
              labelDisplayedRows={({ from, to, count }) =>
                limit === 'all'
                  ? `Semua ${count} pelanggan`
                  : `${from}–${to} dari ${count}`
              }
            />
          )}
        </CardContent>
      </Card>

      {/* Add Customer Dialog */}
      <AddCustomerDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
      />

      {/* Edit Customer Dialog */}
      <EditCustomerDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedCustomer(null);
        }}
        customer={selectedCustomer}
      />

      {/* View Customer Dialog */}
      <ViewCustomerDialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false);
          setSelectedCustomer(null);
        }}
        customer={selectedCustomer}
      />

      <CustomerStatusSyncDialog
        open={syncDialogOpen}
        onClose={() => setSyncDialogOpen(false)}
        customerIds={
          selectedIds.length > 0 ? selectedIds : customers.map((c: any) => c.id)
        }
        scopeLabel={
          selectedIds.length > 0
            ? `${selectedIds.length} pelanggan dipilih`
            : 'Pelanggan di halaman ini (sesuai filter)'
        }
        onFixed={() => refetch()}
        getStatusLabel={getStatusLabel}
      />

      <Dialog open={bulkDialogOpen} onClose={() => !bulkLoading && setBulkDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Ubah status massal</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {bulkCount} pelanggan akan diubah statusnya. PPP di MikroTik akan
            diaktifkan jika status <strong>Aktif</strong>, dinonaktifkan untuk status lainnya.
          </Typography>
          {bulkMessage && (
            <Alert severity={bulkMessage.type} sx={{ mb: 2 }}>
              {bulkMessage.text}
            </Alert>
          )}
          <FormControl fullWidth size="small">
            <InputLabel>Status baru</InputLabel>
            <Select
              value={bulkStatus}
              label="Status baru"
              onChange={(e) => setBulkStatus(e.target.value)}
            >
              <MenuItem value="active">Aktif</MenuItem>
              <MenuItem value="inactive">Non-aktif</MenuItem>
              <MenuItem value="suspended">Ditangguhkan</MenuItem>
              <MenuItem value="free">Gratis</MenuItem>
              <MenuItem value="NonInternet">Non-Internet</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setBulkDialogOpen(false)} disabled={bulkLoading}>
            Tutup
          </Button>
          <Button
            variant="contained"
            onClick={handleBulkStatusUpdate}
            disabled={bulkLoading || !bulkStatus}
          >
            {bulkLoading ? 'Memproses...' : 'Terapkan'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Excel Import Dialog */}
      <ExcelImportDialog
        open={excelImportDialogOpen}
        onClose={() => setExcelImportDialogOpen(false)}
        onImportComplete={() => {
          refetch(); // Refresh customer data after import
        }}
      />
    </Box>
  );
};

export default Customers;
